import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { useSubmitContactMutation } from "@/state/submissionsApi";

const getSubjects = (t: (key: string) => string) => [
  t("subjectCompanyReg"),
  t("subjectCompliance"),
  t("subjectInvestment"),
  t("subjectTender"),
  t("subjectExpansion"),
  t("subjectGeneral"),
];

const ContactSchema = z.object({
  full_name: z.string().min(1, "Name is required"),
  email: z.email("Invalid email address"),
  phone: z.string().optional(),
  subject: z.string().min(1, "Please select a subject"),
  company: z.string().optional(),
  message: z.string().min(1, "Message is required").max(500, "Maximum 500 characters"),
});

type ContactFormValues = z.infer<typeof ContactSchema>;

type Status = "idle" | "success";

export default function ContactForm() {
  const { t } = useTranslation();
  const subjects = getSubjects(t);
  const [status, setStatus] = useState<Status>("idle");
  const [submitError, setSubmitError] = useState("");
  const [submitContact, { isLoading }] = useSubmitContactMutation();

  const { register, handleSubmit, formState: { errors }, watch, reset } = useForm<ContactFormValues>({
    resolver: zodResolver(ContactSchema),
    defaultValues: { full_name: "", email: "", phone: "", subject: "", company: "", message: "" },
  });

  const messageLength = watch("message")?.length ?? 0;

  const onSubmit = async (data: ContactFormValues) => {
    setSubmitError("");
    try {
      await submitContact({
        full_name: data.full_name,
        email: data.email,
        subject: data.subject,
        message: data.message,
        phone: data.phone || undefined,
        company: data.company || undefined,
      }).unwrap();
      setStatus("success");
      reset();
    } catch (err) {
      const message = (err as any)?.data?.error?.message;
      setSubmitError(message ?? t("formError"));
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-100 p-8 lg:p-10">
      <h2 className="text-2xl font-bold text-gray-900 mb-1">{t("formTitle")}</h2>
      <p className="text-gray-500 text-sm mb-8">{t("formDesc")}</p>

      {status === "success" && (
        <div className="mb-6 flex items-start gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
          <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-checkbox-circle-fill text-green-500 text-base" />
          </div>
          <div>
            <p className="text-green-800 text-sm font-semibold">{t("formSuccessTitle")}</p>
            <p className="text-green-700 text-xs mt-0.5">{t("formSuccessBody")}</p>
          </div>
        </div>
      )}

      {submitError && (
        <div className="mb-6 flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-error-warning-fill text-red-500 text-base" />
          </div>
          <p className="text-red-700 text-sm">{submitError}</p>
        </div>
      )}

      <form id="contact-form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="full_name" className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              {t("formName")} <span className="text-red-400">*</span>
            </label>
            <input
              id="full_name"
              type="text"
              placeholder="Jane Doe"
              {...register("full_name")}
              className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-300 focus:outline-none focus:border-[#00C2C2] focus:ring-1 focus:ring-[#00C2C2]/20 transition-colors"
            />
            {errors.full_name && <p className="text-xs text-red-500">{errors.full_name.message}</p>}
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="email" className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              {t("formEmail")} <span className="text-red-400">*</span>
            </label>
            <input
              id="email"
              type="email"
              placeholder="jane@company.com"
              {...register("email")}
              className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-300 focus:outline-none focus:border-[#00C2C2] focus:ring-1 focus:ring-[#00C2C2]/20 transition-colors"
            />
            {errors.email && <p className="text-xs text-red-500">{errors.email.message}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="phone" className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              {t("formPhone")}
            </label>
            <input
              id="phone"
              type="tel"
              placeholder="+250 7XX XXX XXX"
              {...register("phone")}
              className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-300 focus:outline-none focus:border-[#00C2C2] focus:ring-1 focus:ring-[#00C2C2]/20 transition-colors"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="subject" className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              {t("formSubject")} <span className="text-red-400">*</span>
            </label>
            <select
              id="subject"
              {...register("subject")}
              defaultValue=""
              className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#00C2C2] focus:ring-1 focus:ring-[#00C2C2]/20 transition-colors appearance-none bg-white cursor-pointer"
            >
              <option value="" disabled>{t("formSelectTopic")}</option>
              {subjects.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            {errors.subject && <p className="text-xs text-red-500">{errors.subject.message}</p>}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="company" className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            {t("formCompany")}
          </label>
          <input
            id="company"
            type="text"
            placeholder="Your company name"
            {...register("company")}
            className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-300 focus:outline-none focus:border-[#00C2C2] focus:ring-1 focus:ring-[#00C2C2]/20 transition-colors"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label htmlFor="message" className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              {t("formMessage")} <span className="text-red-400">*</span>
            </label>
            <span className={`text-xs ${messageLength > 480 ? "text-red-500" : "text-gray-400"}`}>
              {messageLength}/500
            </span>
          </div>
          <textarea
            id="message"
            rows={5}
            maxLength={500}
            placeholder={t("formMessagePlaceholder")}
            {...register("message")}
            className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-300 focus:outline-none focus:border-[#00C2C2] focus:ring-1 focus:ring-[#00C2C2]/20 transition-colors resize-none"
          />
          {errors.message && <p className="text-xs text-red-500">{errors.message.message}</p>}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-[#0A1628] hover:bg-[#00AAAA] disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold text-sm py-3.5 rounded-full transition-all flex items-center justify-center gap-2 whitespace-nowrap"
        >
          {isLoading ? (
            <>
              <i className="ri-loader-4-line animate-spin text-base" />
              {t("formSending")}
            </>
          ) : (
            <>
              <i className="ri-send-plane-fill text-base" />
              {t("formSubmit")}
            </>
          )}
        </button>
      </form>
    </div>
  );
}
